import { AggregateUnitData } from 'features/siteView/aggregateSiteData'
import { isNil, mergeWith, uniq, uniqBy } from 'lodash'
import { AhpPlan, GeckoEntryBase, GeckoUnit } from 'types'

type CostByYear = { [year: number]: number }

export type UnitYearlyCosts = {
  [unitID: string]:
    | {
        [year: number]: {
          planBaseCost: number
        }
      }
    | undefined
}

/**
 * Extracts the yearly costs (base plan cost, and lpo cost) for each of the supplied years, for each unit.
 */
export function getYearlyCostsByUnit(
  aggregateData: ReadonlyArray<AggregateUnitData>
): UnitYearlyCosts {
  const dataWithPlans = aggregateData.filter(
    (unitData) => !!unitData.allPlanCosts.length
  )

  return dataWithPlans.reduce<UnitYearlyCosts>((aggCosts, curUnitData) => {
    return {
      ...aggCosts,
      [curUnitData.unitSpecs.unitData.id]:
        calculateUnitYearlyCosts(curUnitData),
    }
  }, {})
}

/**
 * Calculates the yearly costs for a single unit.
 */
function calculateUnitYearlyCosts(
  unitAggData: AggregateUnitData
): UnitYearlyCosts[string] {
  const uniquePlansInUnit = uniqBy(
    unitAggData.allPlanCosts.map(({ plan }) => plan),
    ({ id }) => id
  )
  const yearlyLPOCosts = calculateUnitYearlyLPOCosts(
    uniquePlansInUnit,
    unitAggData.unitSpecs.unitData,
    unitAggData.unitSpecs.mappedCriticality
  )

  const unitYearlyPlanCosts = calculateUnitYearlyPlanCosts(unitAggData)

  const allYearsStr = uniq([
    ...Object.keys(yearlyLPOCosts),
    ...Object.keys(unitYearlyPlanCosts),
  ])

  return allYearsStr.reduce<UnitYearlyCosts[string]>(
    (allYearsCosts, yearStr) => {
      const year = parseInt(yearStr)
      if (isNaN(year)) {
        throw new Error(`Invalid year string: ${yearStr}`)
      }
      return {
        ...allYearsCosts,
        [year]: {
          planBaseCost: unitYearlyPlanCosts[year] ?? 0,
          lpoCost: yearlyLPOCosts[year] ?? 0,
        },
      }
    },
    {}
  )
}

/**
 * Calculates the yearly plan costs for a single unit.
 */
function calculateUnitYearlyPlanCosts(
  unitAggData: AggregateUnitData
): CostByYear {
  const costAndYear = unitAggData.allPlanCosts.map((pc) => ({
    cost: pc.cost,
    year: new Date(pc.action.data.actionDate).getUTCFullYear(),
  }))

  return costAndYear.reduce<CostByYear>((yearAggs, { year, cost }) => {
    return {
      ...yearAggs,
      [year]: (yearAggs[year] ?? 0) + (cost ?? 0),
    }
  }, {})
}

/**
 * Calculates the yearly Loss of Product Opportunity (LPO) costs for a single unit.
 */
function calculateUnitYearlyLPOCosts(
  allUniquePlansInUnit: GeckoEntryBase<AhpPlan>[],
  unit: GeckoUnit,
  unitMappedCriticality: string | undefined
): CostByYear {
  const allCosts = allUniquePlansInUnit
    .map((plan) => {
      return calculateLPOYearlyCosts(plan, unit, unitMappedCriticality)
    })
    .filter((c) => !isNil(c))

  return mergeWith(
    {},
    ...allCosts,
    (a: number, b: number) => (a ?? 0) + (b ?? 0)
  )
}

/**
 * Calculates the Loss of Product Opportunity (LPO) costs for each year in the plan.
 */
function calculateLPOYearlyCosts(
  assetHealthPlan: GeckoEntryBase<AhpPlan>,
  unit: GeckoUnit,
  unitMappedCriticality: string | undefined
): CostByYear {
  const failureProbabilities = assetHealthPlan.data.failureProbabilities
  if (isNil(failureProbabilities) || isNil(unitMappedCriticality)) {
    return {}
  }

  return failureProbabilities.reduce<CostByYear>((allYearsCosts) => {
    return allYearsCosts
  }, {})
}
